import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import client from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { fetchLessonById, createLesson, updateLesson, deleteLesson, generateLessonContent } from '../../api/lessons';
import { fetchLessonStandards, addLessonStandard, removeLessonStandard } from '../../api/standards';
import { fetchTemplateVersion } from '../../api/templates';
import LessonPlanDocument from '../LessonPlanDocument';
import DocumentToolbar from '../DocumentToolbar';
import QuestionStepper from '../QuestionStepper';
import Breadcrumb from '../Breadcrumb/Breadcrumb';
import styles from './LessonEditorPage.module.css';

const PROMPT_SUGGESTIONS = [
  "Create a 45-minute math lesson on fractions for 3rd graders",
  "Design a 5th grade ELA lesson on persuasive writing",
  "Plan a kindergarten lesson on counting to 20",
  "Make a 7th grade science lesson about the water cycle",
];

const initialSections = {};

const loadingMessages = [
  'Crafting your lesson plan...',
  'Designing engaging activities...',
  'Adding learning objectives...',
  'Structuring the lesson flow...',
  'Scheduling lessons on your calendar...',
];

export default function LessonEditorPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !id;
  const presetDate = searchParams.get('date');
  const location = useLocation();
  const seedContext = location.state;

  // Lesson state
  const [lesson, setLesson] = useState(null);
  const [lessonId, setLessonId] = useState(id || null);
  const [sections, setSections] = useState(initialSections);
  const [template, setTemplate] = useState(null);
  const [templateVersionId, setTemplateVersionId] = useState(seedContext?.templateVersionId || null);
  const [scheduledDate, setScheduledDate] = useState(presetDate || '');
  const [conversationId, setConversationId] = useState(null);
  const [pageLoading, setPageLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Standards state
  const [lessonStandards, setLessonStandards] = useState([]);

  // Dirty tracking
  const savedSnapshotRef = useRef(JSON.stringify(initialSections));
  const markCleanOnNextRender = useRef(false);
  const isDirty = useMemo(
    () => JSON.stringify(sections) !== savedSnapshotRef.current,
    [sections]
  );

  // Reset snapshot after state settles (used when lesson is saved via chat)
  useEffect(() => {
    if (markCleanOnNextRender.current) {
      savedSnapshotRef.current = JSON.stringify(sections);
      markCleanOnNextRender.current = false;
    }
  }, [sections]);

  // Validation
  const [validationErrors, setValidationErrors] = useState({});

  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [pendingQuestions, setPendingQuestions] = useState(null);
  const [documentVisible, setDocumentVisible] = useState(!!id);
  const messagesEndRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [activeDocument, setActiveDocument] = useState(null);
  const fileInputRef = useRef(null);
  const autoSentRef = useRef(false);

  // Fetch template when templateVersionId changes
  useEffect(() => {
    if (!templateVersionId) return;
    (async () => {
      try {
        const data = await fetchTemplateVersion(templateVersionId);
        setTemplate(data.data || data);
      } catch {
        // Template fetch failed — LessonPlanDocument will use default template
      }
    })();
  }, [templateVersionId]);

  // Reset all state when navigating to a new lesson
  useEffect(() => {
    if (!id) {
      setLesson(null);
      setLessonId(null);
      setSections(initialSections);
      setScheduledDate(presetDate || '');
      setConversationId(null);
      setMessages([]);
      setInput('');
      setError(null);
      setPageLoading(false);
      setValidationErrors({});
      setLessonStandards([]);
      setPendingQuestions(null);
      setDocumentVisible(false);
      savedSnapshotRef.current = JSON.stringify(initialSections);
    }
  }, [id, presetDate]);

  // Load existing lesson
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const data = await fetchLessonById(id);
        const lessonData = data.data || data;
        setLesson(lessonData);
        const loadedSections = lessonData.sections || {};
        // Load linked standards
        if (lessonData.lessonStandards) {
          setLessonStandards(lessonData.lessonStandards);
        }
        setSections(loadedSections);
        savedSnapshotRef.current = JSON.stringify(loadedSections);
        if (lessonData.templateVersionId) {
          setTemplateVersionId(lessonData.templateVersionId);
        }
        if (lessonData.template) {
          setTemplate(lessonData.template);
        }
        if (lessonData.scheduledDate) {
          setScheduledDate(new Date(lessonData.scheduledDate).toISOString().split('T')[0]);
        }
        if (lessonData.conversationId) {
          setConversationId(lessonData.conversationId);
        }
      } catch (err) {
        setError('Failed to load lesson');
      } finally {
        setPageLoading(false);
      }
    })();
  }, [id]);

  // Auto-generate lesson content if seeded from wizard (single API call, no chat)
  useEffect(() => {
    if (autoSentRef.current) return;
    if (pageLoading) return;
    if (!lessonId) return;
    if (!seedContext?.standardCodes?.length && !seedContext?.lessonTopic) return;

    autoSentRef.current = true;

    // Clear route state to prevent re-generate on refresh
    window.history.replaceState({}, '', window.location.pathname);

    setChatLoading(true);
    setDocumentVisible(true);

    generateLessonContent(lessonId, { lessonTopic: seedContext.lessonTopic || undefined })
      .then((result) => {
        const generated = result.data?.sections || result.sections || {};
        setSections(generated);
        savedSnapshotRef.current = JSON.stringify(generated);
        setMessages([{
          role: 'assistant',
          text: `Your lesson plan is ready! Feel free to ask me to modify any section.`,
        }]);
      })
      .catch(() => {
        setMessages([{
          role: 'assistant',
          text: 'I had trouble generating the lesson automatically. You can describe what you need in the chat and I\'ll help build it.',
        }]);
      })
      .finally(() => {
        setChatLoading(false);
      });
  }, [pageLoading, lessonId, seedContext]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-scroll chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, chatLoading]);

  // Cycle loading messages
  useEffect(() => {
    if (chatLoading) {
      const interval = setInterval(() => {
        setLoadingMessageIndex(prev => (prev + 1) % loadingMessages.length);
      }, 2500);
      return () => clearInterval(interval);
    } else {
      setLoadingMessageIndex(0);
    }
  }, [chatLoading]);

  // Warn on browser close/refresh with unsaved changes
  useEffect(() => {
    if (!isDirty) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Navigation guard helper
  const confirmNavigation = useCallback(() => {
    if (!isDirty) return true;
    return window.confirm('You have unsaved changes. Are you sure you want to leave?');
  }, [isDirty]);

  // Apply lesson plan updates from AI
  const applyLessonPlanUpdates = useCallback((updates) => {
    if (!updates || !Array.isArray(updates)) return;

    // Handle standards updates separately (they go to lessonStandards, not sections)
    const standardsUpdates = updates.filter(u => u.section === 'standards');
    const nonLessonSections = new Set(['standards', 'agentChoices', 'batchCreated', 'lessonSaved']);
    const otherUpdates = updates.filter(u => !nonLessonSections.has(u.section));

    if (standardsUpdates.length > 0) {
      setLessonStandards(prev => {
        const existing = new Set(prev.map(ls => (ls.standard?.standardCode || ls.standardCode)));
        const newStandards = [...prev];
        standardsUpdates.forEach(update => {
          const std = update.content;
          if (std && std.standardCode && !existing.has(std.standardCode)) {
            newStandards.push({ standardId: std.id, standard: std, isPrimary: false });
            existing.add(std.standardCode);
          }
        });
        return newStandards;
      });
    }

    if (otherUpdates.length > 0) {
      setSections(prev => {
        const newSections = { ...prev };
        otherUpdates.forEach(update => {
          const { section, content, action = 'set' } = update;
          if (action === 'clear') {
            newSections[section] = Array.isArray(newSections[section]) ? [] : '';
          } else if (action === 'append' && Array.isArray(newSections[section])) {
            newSections[section] = [...newSections[section], ...(Array.isArray(content) ? content : [content])];
          } else {
            newSections[section] = content;
          }
        });
        return newSections;
      });
    }

    // Clear validation errors for AI-updated sections
    const updatedSections = otherUpdates.map(u => u.section);
    setValidationErrors(prev => {
      const next = { ...prev };
      let changed = false;
      updatedSections.forEach(s => {
        if (next[s]) { delete next[s]; changed = true; }
      });
      return changed ? next : prev;
    });
  }, []);

  const handleSectionUpdate = useCallback((section, value) => {
    setSections(prev => ({ ...prev, [section]: value }));
    setValidationErrors(prev => {
      if (prev[section]) {
        const next = { ...prev };
        delete next[section];
        return next;
      }
      return prev;
    });
  }, []);

  const handleClearDocument = useCallback(() => {
    if (window.confirm('Are you sure you want to clear the lesson plan document?')) {
      setSections(initialSections);
    }
  }, []);

  // Standards handlers
  const handleAddStandard = useCallback(async (standard) => {
    if (!lessonId) {
      // Optimistically add to local state for unsaved lessons
      setLessonStandards(prev => [...prev, { standardId: standard.id, standard, isPrimary: false }]);
      return;
    }
    try {
      const result = await addLessonStandard(lessonId, { standardId: standard.id });
      setLessonStandards(prev => [...prev, result.data]);
    } catch (err) {
      console.error('Failed to add standard:', err);
    }
  }, [lessonId]);

  const handleRemoveStandard = useCallback(async (standardId) => {
    if (!lessonId) {
      setLessonStandards(prev => prev.filter(ls => (ls.standard?.id || ls.standardId) !== standardId));
      return;
    }
    try {
      await removeLessonStandard(lessonId, standardId);
      setLessonStandards(prev => prev.filter(ls => (ls.standard?.id || ls.standardId) !== standardId));
    } catch (err) {
      console.error('Failed to remove standard:', err);
    }
  }, [lessonId]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only PDF and image files (JPEG, PNG, WebP, GIF) are supported');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('File must be under 5MB');
      return;
    }
    setSelectedFile(file);
    e.target.value = '';
  };

  const removeSelectedFile = () => setSelectedFile(null);

  const removeActiveDocument = async () => {
    if (activeDocument?.lessonId) {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        await client.delete('/api/chat/document', { headers, params: { lessonId: activeDocument.lessonId } });
      } catch (e) { /* best effort */ }
    }
    setActiveDocument(null);
  };

  // Chat send
  const sendMessageWithText = async (text) => {
    const userMessage = { role: 'user', text, fileName: selectedFile?.name };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setChatLoading(true);
    setPendingQuestions(null);

    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      let response;
      if (selectedFile) {
        const formData = new FormData();
        formData.append('message', text);
        if (conversationId) formData.append('conversationId', conversationId);
        if (lessonId) formData.append('lessonId', String(lessonId));
        formData.append('document', selectedFile);
        response = await client.post('/api/chat', formData, { headers });
        setActiveDocument({ name: selectedFile.name, lessonId });
        setSelectedFile(null);
      } else {
        // Build context message for AI if editing existing lesson
        const contextPayload = {
          message: text,
          conversationId,
          ...(templateVersionId ? { templateVersionId } : {}),
        };

        // If this is the first message and we have an existing lesson, include context
        if (messages.length === 0 && sections.title) {
          contextPayload.lessonContext = {
            title: sections.title,
            subject: sections.subject,
            gradeLevel: sections.grade_level,
            duration: sections.duration,
          };
        }

        response = await client.post('/api/chat', contextPayload, { headers });
      }

      setMessages(prev => [...prev, {
        role: 'assistant',
        text: response.data.text,
      }]);

      if (response.data.conversationId) {
        setConversationId(response.data.conversationId);
      }

      if (response.data.lessonPlanUpdates) {
        applyLessonPlanUpdates(response.data.lessonPlanUpdates);

        // Check for agent choice questions (use last one if multiple)
        const choicesUpdates = response.data.lessonPlanUpdates.filter(
          u => u.section === 'agentChoices'
        );
        const choicesUpdate = choicesUpdates.length > 0 ? choicesUpdates[choicesUpdates.length - 1] : null;
        if (choicesUpdate) {
          setPendingQuestions(choicesUpdate.content.questions);
        } else {
          setPendingQuestions(null);
        }

        // Show document panel on template updates
        const hasTemplateUpdate = response.data.lessonPlanUpdates.some(
          u => u.section !== 'agentChoices' && u.section !== 'batchCreated' && u.section !== 'standards'
        );
        if (hasTemplateUpdate) {
          setDocumentVisible(true);
        }

        // Check if a single lesson was saved via generate_lesson
        const savedUpdate = response.data.lessonPlanUpdates.find(u => u.section === 'lessonSaved');
        if (savedUpdate) {
          const { id: savedId, scheduledDate: savedDate } = savedUpdate.content;
          setLessonId(savedId);
          if (savedDate) {
            setScheduledDate(new Date(savedDate).toISOString().split('T')[0]);
          }
          // Mark clean after sections state settles from applyLessonPlanUpdates
          markCleanOnNextRender.current = true;
          // Update URL without remounting (preserves chat history)
          window.history.replaceState(null, '', `/lessons/${savedId}/edit`);
        }

        // Check if batch lessons were created
        const batchUpdate = response.data.lessonPlanUpdates.find(u => u.section === 'batchCreated');
        if (batchUpdate) {
          const { count } = batchUpdate.content;
          const scheduled = batchUpdate.content.lessons.filter(l => l.scheduledDate).length;
          const msg = scheduled > 0
            ? `${count} lesson${count !== 1 ? 's' : ''} created and ${scheduled} scheduled on your calendar!`
            : `${count} lesson${count !== 1 ? 's' : ''} created as drafts!`;
          setSuccessMessage(msg);
          setTimeout(() => setSuccessMessage(null), 8000);
        }
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'Sorry, something went wrong. Please try again.',
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;
    await sendMessageWithText(input.trim());
  };

  const handleQuestionsComplete = useCallback((answers) => {
    const parts = [];
    if (pendingQuestions) {
      pendingQuestions.forEach((q, i) => {
        if (answers[i] !== null) {
          // answers[i] can be a string or an array of strings
          const val = Array.isArray(answers[i]) ? answers[i].join(' and ') : answers[i];
          parts.push(val);
        }
      });
    }

    setPendingQuestions(null);

    if (parts.length === 0) {
      sendMessageWithText("I'd rather describe what I need in my own words.");
      return;
    }

    sendMessageWithText(parts.join(', '));
  }, [pendingQuestions]);

  useEffect(() => {
    return () => {
      if (activeDocument?.lessonId) {
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        client.delete('/api/chat/document', { headers, params: { lessonId: activeDocument.lessonId } }).catch(() => {});
      }
    };
  }, [activeDocument]);

  // Save lesson
  const handleSave = async () => {
    const errors = {};
    if (!sections.title || !String(sections.title).trim()) errors.title = true;
    if (!sections.subject || !String(sections.subject).trim()) errors.subject = true;

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError('Title and subject are required to save');
      // Scroll to first error field in the document panel
      setTimeout(() => {
        const firstError = document.querySelector('.field-error');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 50);
      return;
    }

    setValidationErrors({});
    setSaving(true);
    setError(null);

    const payload = {
      sections,
      scheduledDate: scheduledDate || undefined,
      conversationId: conversationId || undefined,
    };

    // Include templateVersionId on create
    if (!lessonId && templateVersionId) {
      payload.templateVersionId = templateVersionId;
    }

    try {
      if (lessonId) {
        await updateLesson(lessonId, payload);
      } else {
        if (templateVersionId) {
          payload.templateVersionId = templateVersionId;
        }
        const result = await createLesson(payload);
        const newLesson = result.data || result;
        setLessonId(newLesson.id);
        // Save any pending standards for the new lesson
        for (const ls of lessonStandards) {
          try {
            await addLessonStandard(newLesson.id, { standardId: ls.standard?.id || ls.standardId });
          } catch (err) {
            console.error('Failed to save standard:', err);
          }
        }
        // Reload standards from server
        try {
          const stdResult = await fetchLessonStandards(newLesson.id);
          setLessonStandards(stdResult.data || []);
        } catch {}
        // Update URL to reflect the saved lesson
        navigate(`/lessons/${newLesson.id}/edit`, { replace: true });
      }
      // Mark current state as saved
      savedSnapshotRef.current = JSON.stringify(sections);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      setError('Failed to save lesson');
    } finally {
      setSaving(false);
    }
  };

  // Delete lesson
  const handleDelete = async () => {
    if (!lessonId) return;
    if (!window.confirm(`Are you sure you want to delete "${sections.title || 'this lesson'}"?`)) return;
    try {
      await deleteLesson(lessonId);
      navigate('/calendar');
    } catch (err) {
      setError('Failed to delete lesson');
    }
  };

  if (pageLoading) {
    return (
      <div className={styles.pageLoading}>
        <div className={styles.pageSpinner} />
        <span>Loading lesson...</span>
      </div>
    );
  }

  const welcomeMessage = isNew && presetDate
    ? `Tell me about the lesson you want to create for ${new Date(presetDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}!`
    : isNew
    ? 'Tell me about the lesson you want to create!'
    : `This lesson is loaded. Ask me to modify any section, add content, or refine the plan.`;

  const exampleText = '"Add a warm-up activity" or "Make the assessment more detailed"';

  // Extract display values from sections for breadcrumb/navigation
  const lessonTitle = sections.title || '';
  const lessonSubject = sections.subject || '';

  return (
    <div className={`${styles.container} ${!documentVisible ? styles.chatOnly : ''}`}>
      {/* Chat Panel */}
      <div className={`${styles.chatPanel} ${!documentVisible ? styles.chatPanelFull : ''}`}>
        <div className={styles.chatHeader}>
          <div className={styles.headerIcon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h2 className={styles.chatTitle}>
            {isNew ? 'Create Lesson' : 'Edit Lesson'}
          </h2>
          {lesson?.classId ? (
            <Breadcrumb items={[
              { label: 'Classes', to: '/classes' },
              { label: lesson.class?.name || 'Class', to: `/classes/${lesson.classId}` },
              ...(lesson.unitId && lesson.unit ? [{ label: lesson.unit.title }] : []),
              { label: lessonTitle || 'Lesson' },
            ]} />
          ) : (
            <Link to="/calendar" onClick={(e) => { if (!confirmNavigation()) e.preventDefault(); }} className={styles.backLink}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Calendar
            </Link>
          )}
        </div>

        {error && (
          <div className={styles.errorBanner}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            {error}
          </div>
        )}

        {successMessage && (
          <div className={styles.successBanner}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            {successMessage}
            <Link to="/calendar" className={styles.successLink}>View Calendar</Link>
          </div>
        )}

        {/* Messages */}
        <div className={styles.messagesContainer}>
          {messages.length === 0 && (
            <div className={styles.welcomeContainer}>
              <img
                src="/logo-notext.svg"
                alt="Teachio"
                className={styles.welcomeIcon}
                style={{ width: '64px', height: '64px', objectFit: 'contain' }}
              />
              <h3 className={styles.welcomeTitle}>Lesson Plan Assistant</h3>
              <p className={styles.welcomeText}>{welcomeMessage}</p>
              {isNew ? (
                <div className={styles.suggestionsGrid}>
                  {PROMPT_SUGGESTIONS.map((prompt, i) => (
                    <button
                      key={i}
                      type="button"
                      className={styles.suggestionChip}
                      onClick={() => sendMessageWithText(prompt)}
                      disabled={chatLoading}
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              ) : (
                <div className={styles.examplePrompt}>{exampleText}</div>
              )}
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`${styles.message} ${msg.role === 'user' ? styles.userMessage : styles.assistantMessage}`}
            >
              <div className={styles.messageAvatar}>
                {msg.role === 'user' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
                  </svg>
                )}
              </div>
              <div className={styles.messageContent}>
                <span className={styles.messageRole}>
                  {msg.role === 'user' ? 'You' : 'Assistant'}
                </span>
                <p className={styles.messageText}>{msg.text}</p>
                {msg.fileName && (
                  <span className={styles.fileBadge}>📄 {msg.fileName}</span>
                )}
              </div>
            </div>
          ))}

          {pendingQuestions && !chatLoading && (
            <div className={styles.choiceContainer}>
              <QuestionStepper
                questions={pendingQuestions}
                onComplete={handleQuestionsComplete}
                disabled={chatLoading}
              />
            </div>
          )}

          {chatLoading && (
            <div className={styles.loadingBubble}>
              <div className={styles.loadingSpinner}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--primary-500)" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                  <path d="M12 2a10 10 0 0 1 10 10" />
                </svg>
              </div>
              <span className={styles.loadingText}>{loadingMessages[loadingMessageIndex]}</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* File indicators */}
        {selectedFile && (
          <div className={styles.fileChip}>
            <span className={styles.fileIcon}>📄</span>
            <span className={styles.fileName}>{selectedFile.name}</span>
            <button type="button" className={styles.fileRemove} onClick={removeSelectedFile}>×</button>
          </div>
        )}
        {activeDocument && !selectedFile && (
          <div className={styles.activeDoc}>
            <span>📎 Document active: {activeDocument.name}</span>
            <button type="button" className={styles.fileRemove} onClick={removeActiveDocument}>×</button>
          </div>
        )}

        {/* Input */}
        <div className={styles.inputContainer}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp,.gif"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className={styles.attachBtn}
            onClick={() => fileInputRef.current?.click()}
            disabled={chatLoading}
            title="Attach a document"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            placeholder="Describe changes or ideas..."
            className={styles.chatInput}
            disabled={chatLoading}
          />
          <button
            onClick={sendMessage}
            disabled={chatLoading || !input.trim()}
            className={`${styles.sendButton} ${chatLoading || !input.trim() ? styles.sendButtonDisabled : ''}`}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>

      {/* Document Panel — only shown after template creation starts */}
      {documentVisible && (
        <div className={styles.documentPanel}>
          <DocumentToolbar onClear={handleClearDocument} />
          <div className={styles.documentWrapper}>
            <LessonPlanDocument
              template={template}
              sections={sections}
              onUpdate={handleSectionUpdate}
              validationErrors={validationErrors}
              lessonId={lessonId}
              lessonStandards={lessonStandards}
              onAddStandard={handleAddStandard}
              onRemoveStandard={handleRemoveStandard}
            />
          </div>
          <div className={styles.saveBar}>
            <span className={styles.saveInfo}>
              {scheduledDate && `Scheduled: ${new Date(scheduledDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}`}
              {!scheduledDate && lessonId && 'Draft (no date scheduled)'}
              {!scheduledDate && !lessonId && 'New lesson'}
            </span>
            <div className={styles.saveActions}>
              {lessonId && (
                <button className={styles.newLessonButton} onClick={() => { if (confirmNavigation()) navigate('/lessons/new'); }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  New Lesson
                </button>
              )}
              {lessonId && (
                <button className={styles.worksheetButton} onClick={() => {
                  navigate('/worksheets/new', {
                    state: {
                      lessonId: lessonId,
                      subject: lessonSubject,
                      gradeLevel: sections.grade_level,
                      standardCodes: lessonStandards.map(ls => ls.standard?.standardCode).filter(Boolean),
                      topic: lessonTitle
                    }
                  });
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  Create Worksheet
                </button>
              )}
              {lessonId && (
                <button className={styles.deleteButton} onClick={handleDelete}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  Delete
                </button>
              )}
              <button
                className={`${styles.saveButton} ${justSaved ? styles.saveButtonSaved : ''}`}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                      <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                      <path d="M12 2a10 10 0 0 1 10 10" />
                    </svg>
                    Saving...
                  </>
                ) : justSaved ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Saved!
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    {lessonId ? 'Save Changes' : 'Save Lesson'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
